import type { ColumnDef } from "@tanstack/react-table";
import { ShiftRepeat } from "@repo/database";
import { highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format, isAfter } from "date-fns";
import { useTranslation } from "react-i18next";

import type { OrderShift } from "~/providers/shift-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useShift } from "~/hooks/use-shift";

export const Route = createFileRoute(
  "/_main/settings/_layout/online-orders/shifts/",
)({
  component: Shifts,
});

function Shifts() {
  const { orderShifts, openOrderShiftDialog } = useShift();

  const { t } = useTranslation("common");

  const days = [
    {
      value: 0,
      label: t("Days.sunday"),
    },
    {
      value: 1,
      label: t("Days.monday"),
    },
    {
      value: 2,
      label: t("Days.tuesday"),
    },
    {
      value: 3,
      label: t("Days.wednesday"),
    },
    {
      value: 4,
      label: t("Days.thursday"),
    },
    {
      value: 5,
      label: t("Days.friday"),
    },
    {
      value: 6,
      label: t("Days.saturday"),
    },
  ];

  const columns: ColumnDef<OrderShift>[] = [
    {
      id: t("recurrence"),
      accessorKey: "startDate",
      filterFn: "includesString",
      size: 250,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("recurrence")} />
      ),
      cell: ({ row: { original: shift }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        if (shift.repeat === ShiftRepeat.never) {
          return (
            <p>
              {t("Shift.recurrence_never", {
                date: format(shift.startDate, "PPPP"),
              })}
            </p>
          );
        } else if (shift.repeat === ShiftRepeat.weekly) {
          if (isAfter(shift.startDate, new Date())) {
            if (shift.endDate) {
              return (
                <p>
                  {t("Shift.recurrence_weekly_from_until", {
                    from: format(shift.startDate, "PP"),
                    until: format(shift.endDate, "PP"),
                    week: days
                      .filter((day) => shift.repeatDays.includes(day.value))
                      .sort((a, b) => {
                        // Define the custom order for sorting
                        const order = [1, 2, 3, 4, 5, 6, 0]; // Monday (1) to Sunday (0)
                        return order.indexOf(a.value) - order.indexOf(b.value);
                      })
                      .map((d) => d.label)
                      .join(", ")
                      .toLowerCase(),
                  })}
                </p>
              );
            }
            return (
              <p>
                {t("Shift.recurrence_weekly_from", {
                  from: format(shift.startDate, "PP"),
                  week: days
                    .filter((day) => shift.repeatDays.includes(day.value))
                    .sort((a, b) => {
                      // Define the custom order for sorting
                      const order = [1, 2, 3, 4, 5, 6, 0]; // Monday (1) to Sunday (0)
                      return order.indexOf(a.value) - order.indexOf(b.value);
                    })
                    .map((d) => d.label)
                    .join(", ")
                    .toLowerCase(),
                })}
              </p>
            );
          }
          if (shift.endDate) {
            return (
              <p>
                {t("Shift.recurrence_weekly_until", {
                  end_date: format(shift.endDate, "PP"),
                  week: days
                    .filter((day) => shift.repeatDays.includes(day.value))
                    .sort((a, b) => {
                      // Define the custom order for sorting
                      const order = [1, 2, 3, 4, 5, 6, 0]; // Monday (1) to Sunday (0)
                      return order.indexOf(a.value) - order.indexOf(b.value);
                    })
                    .map((d) => d.label)
                    .join(", ")
                    .toLowerCase(),
                })}
              </p>
            );
          }
          return (
            <p>
              {t("Shift.recurrence_weekly", {
                week: days
                  .filter((day) => shift.repeatDays.includes(day.value))
                  .sort((a, b) => {
                    // Define the custom order for sorting
                    const order = [1, 2, 3, 4, 5, 6, 0]; // Monday (1) to Sunday (0)
                    return order.indexOf(a.value) - order.indexOf(b.value);
                  })
                  .map((d) => d.label)
                  .join(", ")
                  .toLowerCase(),
              })}
            </p>
          );
        }
        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  `${format(shift.startTime, "p")} - ${format(shift.endTime, "p")}`,
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("time"),
      accessorKey: "startTime",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("time")} />
      ),
      cell: ({ row: { original: shift }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  `${format(shift.startTime, "p")} - ${format(shift.endTime, "p")}`,
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("OnlineOrders.Shifts.order_type"),
      accessorKey: "orderType",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("OnlineOrders.Shifts.order_type")}
        />
      ),
      cell: ({ row: { original: shift }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(`${shift.orderType}`, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("OnlineOrders.Shifts.page_title")}
        buttonLabel={t("new_shift")}
        buttonAction={() => {
          openOrderShiftDialog();
        }}
      />
      <DataTable
        defaultSorting={[{ id: t("recurrence"), desc: false }]}
        columns={columns}
        data={orderShifts.filter((shift) => shift.closed === false)}
        onSelect={openOrderShiftDialog}
      />
    </>
  );
}
